import { useState, useEffect, useCallback } from 'react';
import { getAllFours,getFourById, updateFour,createFour,deleteFour } from '../models/fourAPI';
import { bulkUploadFournisseurs, uploadFournisseursDBF } from '../models/fourAPI';

export const useFetchAllFours = () => {
  const [Fours, setFours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFours = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getAllFours();
      setFours(response.data);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFours();
  }, [fetchFours]);

  return { Fours, loading, error, refetch: fetchFours };
};



export const useFetchFourById = (id) => {
  const [Four, setFour] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Define the fetching logic as a callable function using useCallback
  const fetchFour = useCallback(() => {
    if (id) {
      setLoading(true);
      setError(''); // Reset error state before new fetch attempt
      getFourById(id)
        .then(response => {
          setFour(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [id]);

  // Invoke fetchFour when the component mounts and when id changes
  useEffect(() => {
    fetchFour();
  }, [fetchFour]);

  // Return all the state management vars and the refetch function
  return { Four, loading, error, refetch: fetchFour };
};



export const useUpdateFour = () => {
  const [error, setError] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const handleUpdate = async (id, FourData) => {
    try {
      await updateFour(id, FourData);
      setIsUpdated(true);
    } catch (err) {
      setError(err.message);
      setIsUpdated(false);
    }
  };

  return { handleUpdate, error, isUpdated };
};


export const useCreateFour = () => {
  const [error, setError] = useState('');
  const [isCreated, setIsCreated] = useState(false);

  const handleCreate = async (FourData) => {
    try {
      await createFour(FourData);
      setIsCreated(true);
    } catch (err) {
      setError(err.message);
      setIsCreated(false);
    }
  };

  return { handleCreate, error, isCreated };
};


export const useDeleteFour = () => {
  const [error, setError] = useState('');
  const [isDeleted, setIsDeleted] = useState(false);

  const handleDelete = async (id) => {
    try {
      await deleteFour(id);
      setIsDeleted(true);
    } catch (err) {
      setError(err.message);
      setIsDeleted(false);
    }
  };

  return { handleDelete, error, isDeleted };
};


export const useBulkUploadFournisseurs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleBulkUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await bulkUploadFournisseurs(formData);
      setUploadResult(response.data);
    } catch (err) {
      setError(err.message || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  return { handleBulkUpload, loading, error, uploadResult };
};

// Seed DBF upload hook
export const useUploadFournisseursDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await uploadFournisseursDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};

